'use client'

import {
  BalloonAnalogueRiskTask as BalloonAnalogueRiskTaskType,
  QuestionType
} from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { cn } from '@/utils'
import * as React from 'react'
import { BalloonStage } from './components/BalloonStage'
import { IntroStage } from './components/IntroStage'
import { ResultsStage } from './components/ResultsStage'
import { useTestResponse } from '@/hooks/useTestResponse'

interface BalloonRiskTaskProps {
  data?: BalloonAnalogueRiskTaskType
  moduleId: string
  questionId: string
  onComplete?: (results: BalloonResult[]) => void
  onStart?: () => void
  className?: string
}

export interface BalloonResult {
  balloonNumber: number
  pumps: number
  popped: boolean
  earnings: number
}

type TaskStage = 'intro' | 'balloon' | 'cashout' | 'results'

export function BalloonRiskTask({
  data,
  moduleId,
  questionId,
  onComplete,
  onStart,
  className
}: BalloonRiskTaskProps) {
  const [stage, setStage] = React.useState<TaskStage>('intro')
  const [currentBalloon, setCurrentBalloon] = React.useState(1)
  const [pumpCount, setPumpCount] = React.useState(0)
  const [currentEarnings, setCurrentEarnings] = React.useState(0)
  const [totalEarnings, setTotalEarnings] = React.useState(0)
  const [results, setResults] = React.useState<BalloonResult[]>([])
  const [isPopped, setIsPopped] = React.useState(false)
  const [isCashedOut, setIsCashedOut] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [balloonSize, setBalloonSize] = React.useState(60)
  const { addQuestionResponse } = useTestResponse()

  React.useEffect(() => {
    startTask()
  }, [])

  const calculatePopProbability = (pumps: number) => {
    if (!data) return 0
    if (pumps === 1) {
      return data.initialBurstProbabilityPercent
    }
    if (pumps === data.maxClicksBeforeBurst) {
      return data.finalBurstProbabilityPercent
    }
    if (!data.balloons) return data.burstProbabilityPercent
    const currentBalloonData = data.balloons.find(
      (b) => b.sequenceNumber === currentBalloon
    )
    if (currentBalloonData && pumps === currentBalloonData.burstAt) {
      return 100
    }
    return data.burstProbabilityPercent
  }

  const checkPop = (pumps: number) => {
    if (!data) return false
    const probability = calculatePopProbability(pumps)
    if (probability === 100) return true
    return Math.random() * 100 < probability
  }

  const startTask = () => {
    if (onStart) {
      onStart()
    }
    setStage('balloon')
    setCurrentBalloon(1)
    setPumpCount(0)
    setCurrentEarnings(0)
    setTotalEarnings(0)
    setResults([])
    setIsPopped(false)
    setIsCashedOut(false)
    setBalloonSize(60)
  }

  const pumpBalloon = () => {
    if (!data) return
    if (pumpCount >= data.maxClicksBeforeBurst) {
      return
    }
    setIsLoading(true)

    setTimeout(() => {
      const newPumpCount = pumpCount + 1
      setPumpCount(newPumpCount)
      const newEarnings = newPumpCount * data.profitPerClick
      if (data.canHaveNegativeBalance || newEarnings >= 0) {
        setCurrentEarnings(newEarnings)
      }
      setBalloonSize(60 + Math.sqrt(newPumpCount) * 20)

      if (checkPop(newPumpCount)) {
        setIsPopped(true)
        setCurrentEarnings(0)

        const result: BalloonResult = {
          balloonNumber: currentBalloon,
          pumps: newPumpCount,
          popped: true,
          earnings: 0
        }
        setResults((prev) => [...prev, result])

        setTimeout(() => {
          moveToNextBalloon()
        }, 1500)
      }

      setIsLoading(false)
    }, 300)
  }

  const cashOut = () => {
    if (!data) return
    setIsLoading(true)

    setTimeout(() => {
      setIsCashedOut(true)
      if (data.canHaveNegativeBalance || currentEarnings >= 0) {
        setTotalEarnings((prev) => prev + currentEarnings)
      }

      const result: BalloonResult = {
        balloonNumber: currentBalloon,
        pumps: pumpCount,
        popped: false,
        earnings: currentEarnings
      }
      setResults((prev) => [...prev, result])

      setTimeout(() => {
        moveToNextBalloon()
      }, 1500)

      setIsLoading(false)
    }, 300)
  }

  const moveToNextBalloon = () => {
    if (!data) return
    if (currentBalloon >= data.balloonCount) {
      setStage('results')
    } else {
      setCurrentBalloon((prev) => prev + 1)
      setPumpCount(0)
      setCurrentEarnings(0)
      setIsPopped(false)
      setIsCashedOut(false)
      setBalloonSize(60)
      setStage('balloon')
    }
  }

  const handleContinue = async () => {
    await addQuestionResponse(moduleId, {
      questionId,
      questionType: QuestionType.BALLOON_ANALOGUE_RISK_TASK,
      balloonTaskAnswer: {
        balloonResults: results.map((result) => ({
          balloonIndex: result.balloonNumber,
          clickCount: result.pumps,
          wasPopped: result.popped
        }))
      }
    })

    if (onComplete) {
      onComplete(results)
    }
  }

  return (
    <Card className={cn('mx-auto w-full max-w-md min-w-[30vw]', className)}>
      <CardContent className="p-6">
        {stage === 'intro' && (
          <IntroStage
            moneyPerPump={data?.profitPerClick || 5}
            startTask={startTask}
            totalBalloons={data?.balloonCount || 10}
          />
        )}

        {stage === 'balloon' && (
          <BalloonStage
            balloonSize={balloonSize}
            currentBalloon={currentBalloon}
            currentEarnings={currentEarnings}
            isCashedOut={isCashedOut}
            isLoading={isLoading}
            isPopped={isPopped}
            maxPumps={data?.maxClicksBeforeBurst || 20}
            moneyPerPump={data?.profitPerClick || 5}
            onCashOut={cashOut}
            onPump={pumpBalloon}
            pumpCount={pumpCount}
            totalBalloons={data?.balloonCount || 10}
            totalEarnings={totalEarnings}
          />
        )}

        {stage === 'results' && (
          <ResultsStage
            onContinue={handleContinue}
            results={results}
            totalEarnings={totalEarnings}
          />
        )}
      </CardContent>
    </Card>
  )
}
